import { authService } from '@/services/AuthService'
import { createRouter, createWebHistory } from 'vue-router'
import HomeView from '@/views/HomeView.vue'
import { useProfileStore } from '@/store/profile.module'
import { Role } from '@/model/Employee'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  },
  routes: [
    {
      path: '/company/:companyId/verifactu',
      name: 'verifactu',
      component: () => import('../views/VerifactuView.vue')
    },
    {
      path: '/',
      name: 'home',
      component: HomeView
    },
    {
      path: '/customers',
      name: 'customers',
      component: () => import('../views/CustomersView.vue')
    },
    {
      path: '/customers/:customerId',
      name: 'customerDetail',
      component: () => import('../views/CustomerDetailView.vue')
    },
    {
      path: '/vehicles',
      name: 'vehicles',
      component: () => import('../views/VehiclesView.vue')
    },
    {
      path: '/vehicles/:vehicleId',
      name: 'vehicleDetail',
      component: () => import('../views/VehicleDetailView.vue')
    },
    {
      path: '/places',
      name: 'places',
      component: () => import('../views/PlacesView.vue')
    },
    {
      path: '/prices',
      name: 'prices',
      component: () => import('../views/PricesView.vue')
    },
    {
      path: '/prices/:priceId',
      name: 'priceDetail',
      component: () => import('../views/PriceDetailView.vue')
    },
    {
      path: '/employees',
      name: 'employees',
      component: () => import('../views/EmployeesView.vue')
    },
    {
      path: '/employee-documents',
      name: 'employeeDocuments',
      component: () => import('../views/EmployeeDocumentsView.vue')
    },
    {
      path: '/employee-documents/:id',
      name: 'employeeDocumentDetail',
      component: () => import('../views/EmployeeDocumentDetailView.vue')
    },
    {
      path: '/vehicle-documents',
      name: 'vehicleDocuments',
      component: () => import('../views/VehicleDocumentsView.vue')
    },
    {
      path: '/vehicle-documents/:id',
      name: 'vehicleDocumentDetail',
      component: () => import('../views/VehicleDocumentDetailView.vue')
    },
    {
      path: '/refuels',
      name: 'refuels',
      component: () => import('../views/RefuelsView.vue')
    },
    {
      path: '/deliveryNotes',
      name: 'deliveryNotes',
      component: () => import('@/views/DeliveryNotesView.vue')
    },
    {
      path: '/deliveryNotes/:deliveryNoteId',
      name: 'deliveryNoteDetail',
      component: () => import('../views/DeliveryNoteDetailView.vue')
    },
    {
      path: '/bills',
      name: 'bills',
      component: () => import('../views/BillsView.vue')
    },
    {
      path: '/bills/:billId',
      name: 'billDetail',
      component: () => import('../views/BillDetailView.vue')
    },
    {
      path: '/newBill',
      name: 'newBill',
      component: () => import('@/views/BillTypeSelectionView.vue')
    },
    {
      path: '/newBill/standard',
      name: 'newStandardBill',
      component: () => import('@/views/NewStandardBillView.vue')
    },
    {
      path: '/newBill/simplified',
      name: 'newSimplifiedBill',
      component: () => import('@/views/NewSimplifiedBillView.vue')
    },
    {
      path: '/expenses',
      name: 'expenses',
      component: () => import('../views/ExpensesView.vue')
    },
    {
      path: '/companies',
      name: 'companies',
      component: () => import('../views/CompaniesView.vue')
    },
    {
      path: '/all-employees',
      name: 'allEmployees',
      component: () => import('../views/AllEmployeesView.vue')
    },
    {
      path: '/subscriptions',
      name: 'subscriptions',
      component: () => import('../views/SubscriptionsSummaryView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/companies/:companyId',
      name: 'companyDetail',
      component: () => import('../views/CompanyDetailView.vue')
    },
    {
      path: '/companies/:companyId/customers/import',
      name: 'adminCompanyCustomerImport',
      component: () => import('../views/companies/import/AdminCompanyCustomerImportView.vue')
    },
    {
      path: '/companies/:companyId/vehicles/import',
      name: 'adminCompanyVehicleImport',
      component: () => import('../views/companies/import/AdminCompanyVehicleImportView.vue')
    },
    {
      path: '/companies/:companyId/places/import',
      name: 'adminCompanyPlaceImport',
      component: () => import('../views/companies/import/AdminCompanyPlaceImportView.vue')
    },
    {
      path: '/companies/:companyId/payloads/import',
      name: 'adminCompanyPayloadImport',
      component: () => import('../views/companies/import/AdminCompanyPayloadImportView.vue')
    },
    {
      path: '/companies/:companyId/employees/import',
      name: 'adminCompanyEmployeeImport',
      component: () => import('../views/companies/import/AdminCompanyEmployeeImportView.vue')
    },
    {
      path: '/companies/:companyId/delivery-notes/:deliveryNoteId',
      name: 'adminDeliveryNoteDetail',
      component: () => import('../views/companies/details/AdminDeliveryNoteDetailView.vue')
    },
    {
      path: '/companies/:companyId/bills/:billId',
      name: 'adminBillDetail',
      component: () => import('../views/companies/details/AdminBillDetailView.vue')
    },
    {
      path: '/companies/:companyId/employees/:employeeId',
      name: 'adminEmployeeDetail',
      component: () => import('../views/companies/details/AdminEmployeeDetailView.vue')
    },
    {
      path: '/companies/:companyId/customers/:customerId',
      name: 'adminCustomerDetail',
      component: () => import('../views/companies/details/AdminCustomerDetailView.vue')
    },
    {
      path: '/companies/:companyId/drivers/:driverId',
      name: 'adminDriverDetail',
      component: () => import('../views/companies/details/AdminDriverDetailView.vue')
    },
    {
      path: '/companies/:companyId/vehicles/:vehicleId',
      name: 'adminVehicleDetail',
      component: () => import('../views/companies/details/AdminVehicleDetailView.vue')
    },
    {
      path: '/companies/:companyId/places/:placeId',
      name: 'adminPlaceDetail',
      component: () => import('../views/companies/details/AdminPlaceDetailView.vue')
    },
    {
      path: '/companies/:companyId/prices/:priceId',
      name: 'adminPriceDetail',
      component: () => import('../views/companies/details/AdminPriceDetailView.vue')
    },
    {
      path: '/companies/:companyId/payloads/:payloadId',
      name: 'adminPayloadDetail',
      component: () => import('../views/companies/details/AdminPayloadDetailView.vue')
    },
    {
      path: '/profile',
      name: 'profile',
      component: () => import('@/views/ProfileView.vue'),
      children: [
        {
          path: 'deliveryNotesConfig',
          name: 'deliveryNotesConfig',
          component: () => import('@/views/DeliveryNotesConfigView.vue')
        },
        {
          path: 'billsConfig',
          name: 'billsConfig',
          component: () => import('@/views/BillsConfigView.vue')
        },
        {
          path: 'subscriptionConfig',
          name: 'subscriptionConfig',
          component: () => import('@/views/SubscriptionConfigView.vue')
        },
        {
          path: 'nimas',
          name: 'nimas',
          component: () => import('../views/NimasView.vue')
        }
      ]
    },
    {
      path: '/news',
      name: 'news',
      component: () => import('../views/NewsView.vue')
    },
    {
      path: '/news/:id',
      name: 'newsDetail',
      component: () => import('../views/NewsDetailView.vue')
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/LoginView.vue')
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('../views/RegisterView.vue')
    },
    {
      path: '/register-company',
      name: 'register-company',
      component: () => import('../views/RegisterCompanyView.vue')
    },
    {
      path: '/terms-and-conditions',
      name: 'terms-and-conditions',
      component: () => import('../views/TermsAndConditionsView.vue')
    },
    {
      path: '/sharedLink',
      name: 'sharedLink',
      component: () => import('../views/SharedLinkView.vue')
    },
    {
      path: '/unauthorized',
      name: 'unauthorized',
      component: () => import('../views/UnauthorizedView.vue')
    },
    {
      path: '/production',
      name: 'production',
      component: () => import('@/views/ProductionDashboardView.vue'),
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/billing-dashboard',
      name: 'billingDashboard',
      component: () => import('@/views/BillingDashboardView.vue'),
      meta: {
        requiresAuth: true
      }
    }
  ]
})

router.beforeResolve(async (to) => {
  const storeProfile = useProfileStore()

  if (to.name === 'terms-and-conditions') {
    // If the user is trying to access the terms and conditions page, allow it
    return true
  }

  const isAuthenticated = await authService.isAuthenticated()

  // make sure the user is authenticated
  // ❗️ Avoid an infinite redirect
  if (!isAuthenticated && to.name !== 'login' && to.name !== 'register' && to.name !== 'sharedLink') {
    // redirect the user to the login page
    return 'login'
  }


  if (isAuthenticated) {
    if (!authService.isEmailAllowed()) {
      if (to.name !== 'unauthorized') {
        return '/unauthorized'
      }
      return true
    }

    if (
      (storeProfile.selectedCompany === undefined || storeProfile.selectedCompany.role === undefined)) {
      if (to.name !== 'register-company') {
        // If the user is authenticated but the selected company doesn't exist, redirect to register company
        return '/register-company'
      }
    } else {
      if (storeProfile.selectedCompany.role !== Role.ADMIN &&
        to.name !== 'unauthorized') {
        // Unauthorized user
        return '/unauthorized'
      }

      if (storeProfile.selectedCompany.role === Role.ADMIN &&
        (to.name === 'login' || to.name === 'unauthorized' || to.name === 'register' || to.name === 'register-company')) {
        // redirect the user to the home page
        return '/'
      }
    }


  }

})

export default router
